// FILE: newThreadDialogStore.ts
// Purpose: Open the "New thread" dialog from anywhere (sidebar buttons,
//          command palette, keyboard shortcuts) without threading props.
// Layer: Web stores
//
// The dialog itself is mounted once, in the chat route layout; the entry
// points only push a request here.

import {
  type EnvironmentId,
  type ProjectId,
  type ProviderKind,
  type QaOperationKind,
} from "@taskin-code/contracts";
import { create } from "zustand";

import type { NewThreadDialogPullRequest } from "./components/NewThreadDialog.logic";

export interface NewThreadDialogRequest {
  /** Project to pre-select; `null` falls back to the first project. */
  preferredProjectId: ProjectId | null;
  /** Set by the per-provider shortcuts (`chat.newClaude` / `chat.newCodex`). */
  initialProvider: ProviderKind | null;
  /**
   * Pull request to pre-select, for the entry points that already know one — the "Crear hilo"
   * button in a pull request's detail, and the branch toolbar. Carried here rather than fetched
   * by the dialog because those callers are looking at the pull request already.
   */
  pullRequest: NewThreadDialogPullRequest | null;
  /**
   * Environment to open the thread in, set by «Abrir hilo aquí» on an
   * environment card. `null` leaves the choice to the dialog's own default
   * (the person's personal environment, then the project's shared one).
   */
  environmentId: EnvironmentId | null;
  /**
   * Set by the environment card's operation buttons («Actualizar con qa-main»,
   * «Resetear datos», «Copiar desde…»). Non-null turns the dialog into its
   * «operación de entorno» mode: the project and the environment stop being
   * choices, and only agents that can actually run the operation are offered.
   *
   * «Abrir hilo aquí» deliberately leaves this null — it is a person opening a
   * conversation in a workspace, not an operation on it, and narrowing the
   * agent list there would take away agents that work perfectly well.
   */
  qaOperation: QaOperationKind | null;
  /**
   * The message the composer opens with, already written. Seeded rather than
   * sent: the operation needs a person to read it and press Send, which is the
   * whole point of routing these buttons through a thread.
   */
  initialPrompt: string | null;
}

interface NewThreadDialogStoreState {
  isOpen: boolean;
  request: NewThreadDialogRequest;
  openNewThreadDialog: (request?: Partial<NewThreadDialogRequest>) => void;
  closeNewThreadDialog: () => void;
}

const EMPTY_REQUEST: NewThreadDialogRequest = {
  preferredProjectId: null,
  initialProvider: null,
  pullRequest: null,
  environmentId: null,
  qaOperation: null,
  initialPrompt: null,
};

export const useNewThreadDialogStore = create<NewThreadDialogStoreState>((set) => ({
  isOpen: false,
  request: EMPTY_REQUEST,
  openNewThreadDialog: (request) =>
    set({
      isOpen: true,
      request: { ...EMPTY_REQUEST, ...request },
    }),
  closeNewThreadDialog: () => set({ isOpen: false }),
}));

/** Imperative opener, for call sites that are not React components. */
export function openNewThreadDialog(request?: Partial<NewThreadDialogRequest>): void {
  useNewThreadDialogStore.getState().openNewThreadDialog(request);
}
